import React, { useCallback, useEffect } from 'react';
import { useHideable } from '@/hooks/behaviours/useHideable';
import { Button, ButtonColor } from '@tv2/ui-system/components/Button';
import { IconButton } from '@tv2/ui-system/components/IconButton';
import { CloseBasicWhiteNext } from '@tv2/ui-system/icons/next/navigation/CloseBasicWhiteNext';
import {
  useNotifiers,
  Notification as NotificationItem,
  NotificationButton,
} from '@/api/notification/useNotifiers';
import { useUnknownTrack } from '@tv2/lib-offspring/hooks/tracking/useUnknownTrack';
import { usePathname } from 'next/navigation';
import { useNotificationAction } from './useNotificationAction';

export type NotificationProps = {
  notification: NotificationItem;
  onCloseDate?: Date;
  localStorage?: Storage;
  sessionStorage?: Storage;
  navigateTo?: (url: string) => void;
  checkoutUrl: string;
};

function navigateToUrl(url: string) {
  if (typeof window !== 'undefined') {
    window.location.href = url;
  }
}

export function Notification({
  notification,
  localStorage = window.localStorage,
  sessionStorage = window.sessionStorage,
  navigateTo = navigateToUrl,
  checkoutUrl,
}: NotificationProps) {
  const { id, trackingInfo, colorMode, description, title, logo, backgroundImage, buttons } =
    notification;
  const trackUnknown = useUnknownTrack();
  const [isHiddenLocal, hideUntil] = useHideable({
    id: `notification_${id}`,
    initial: true,
    externalStorage: localStorage,
  });

  const [isHiddenSesion, hideUntilSession] = useHideable({
    id: `notification_${id}`,
    initial: true,
    externalStorage: sessionStorage,
  });

  const isHidden = isHiddenLocal || isHiddenSesion;

  const pathname = usePathname();

  const trackFunction = useCallback(() => {
    trackUnknown({
      event: trackingInfo.identifier,
      data: { header_text: title, ...trackingInfo.details },
    });
  }, [title, trackingInfo, trackUnknown]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency management
  useEffect(() => {
    if (!isHidden) {
      trackFunction();
    }
  }, [isHidden, pathname, trackFunction]);

  const { dispatch } = useNotificationAction({
    notification,
    hideUntil,
    hideUntilSession,
    navigateTo,
    checkoutUrl,
    tracking: trackingInfo,
  });

  const onClose = async () => {
    const button: NotificationButton = {
      buttonLink: '',
      buttonType: 'CLOSE',
      buttonText: 'close',
      buttonColor: 'primary',
    };
    await dispatch(button);
  };

  if (isHidden) {
    return null;
  }

  return (
    <div
      key={id}
      className={`py-4 mb-4 ${colorMode === 'dark' ? 'bg-midnight-900' : 'bg-white'}`}
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
        backgroundPosition: 'center center',
        backgroundSize: 'cover',
      }}
    >
      <div className="deck">
        <div className="w-col-12 flex flex-col 768:flex-row 768:justify-between 768:items-center">
          <div className="justify-end items-center cursor-pointer md:hidden flex">
            <IconButton
              onClick={onClose}
              icon={CloseBasicWhiteNext}
              size="x-small"
              color="secondary"
              darkMode={colorMode === 'dark'}
              aria-label="luk"
              data-testid="close"
            />
          </div>
          <div className="flex">
            {logo ? (
              <div className="mr-[24px] max-w-[200px] hidden 768:flex items-center">
                <img src={logo} alt="" />
              </div>
            ) : null}
            <div className="flex flex-col justify-center">
              {title ? (
                <h2
                  className="text-list-title-xs lg:text-list-title-s"
                  style={{
                    color: colorMode === 'dark' ? 'white' : 'black',
                  }}
                >
                  {title}
                </h2>
              ) : null}

              {description ? (
                <div
                  className="text-legacy-body-xs lg:text-legacy-body-s mt-2"
                  dangerouslySetInnerHTML={{
                    __html: description,
                  }}
                  style={{
                    color: colorMode === 'dark' ? 'white' : 'black',
                  }}
                ></div>
              ) : null}
            </div>
          </div>
          <div className="mt-4 ml-0 768:mt-0 768:ml-4 flex flex-row justify-center">
            {buttons.map((a: NotificationButton) => {
              return (
                <div className="ml-4">
                  <Button
                    key={a.buttonText}
                    size="medium"
                    onClick={async () => {
                      await dispatch(a);
                    }}
                    color={mapButtonColor(a.buttonColor)}
                    darkMode={colorMode === 'dark'}
                  >
                    {a.buttonText}
                  </Button>
                </div>
              );
            })}

            <div className="ml-4  justify-center items-center cursor-pointer md:flex hidden">
              <IconButton
                onClick={onClose}
                icon={CloseBasicWhiteNext}
                size="small"
                color="secondary"
                darkMode={colorMode === 'dark'}
                aria-label="luk"
                data-testid="close"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function mapButtonColor(color: string): ButtonColor {
  switch (color) {
    case 'primary':
      return 'primary';
    case 'secondary':
      return 'secondary';
    case 'primary-red':
      return 'primary-red';
    case 'tertiary':
      return 'tertiary';
    case 'primary-green':
      return 'primary-green';
    default:
      return 'primary';
  }
}

export function NotificationContainer({ checkoutUrl }: { checkoutUrl: string }) {
  const { notification } = useNotifiers() ?? {};
  if (!notification) return null;
  return <Notification notification={notification} checkoutUrl={checkoutUrl} />;
}
